import React from "react";

/**
 * Renders a forum post's plain-text body and:
 *   - linkifies http(s) URLs as anchors (target=_blank, noopener)
 *   - detects YouTube watch/youtu.be/shorts URLs and inlines an
 *     embedded iframe AFTER the body (one per unique video).
 *   - renders consecutive lines starting with "> " as a blockquote
 *     (used by the Quote-reply feature).
 *
 * Plain-text preserving — whitespace + newlines are kept via
 * white-space:pre-wrap. Emojis (unicode) pass through unchanged.
 */

// Matches http(s)://… up to the next whitespace.
const URL_RE = /(https?:\/\/[^\s<>"']+)/g;

// Pulls a YouTube video ID out of any common YouTube URL shape.
function extractYouTubeId(url) {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      return u.pathname.replace(/^\/+/, "").split("/")[0] || null;
    }
    if (host === "youtube.com" || host === "m.youtube.com") {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      const m = u.pathname.match(/^\/(?:shorts|embed)\/([\w-]{6,})/);
      return m ? m[1] : null;
    }
    return null;
  } catch {
    return null;
  }
}

function linkify(text, keyPrefix = "") {
  if (!text) return [];
  const out = [];
  let last = 0;
  for (const match of text.matchAll(URL_RE)) {
    const idx = match.index ?? 0;
    if (idx > last) out.push(text.slice(last, idx));
    const url = match[0];
    out.push(
      <a
        key={`${keyPrefix}u-${idx}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer nofollow ugc"
        className="break-all text-[#0A0F17] underline underline-offset-2 hover:text-[#6CABDD]"
      >
        {url}
      </a>
    );
    last = idx + url.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Split the body into a sequence of {type: "text"|"quote", lines: string[]}
// blocks. Consecutive lines beginning with "> " become a single quote block;
// everything else gets coalesced into a text block, preserving newlines.
function parseBlocks(body) {
  const blocks = [];
  let buffer = { type: "text", lines: [] };
  const flush = () => {
    if (buffer.lines.length) blocks.push(buffer);
  };
  for (const raw of body.split(/\n/)) {
    const m = raw.match(/^>\s?(.*)$/);
    const isQuote = !!m;
    const line = isQuote ? m[1] : raw;
    const kind = isQuote ? "quote" : "text";
    if (kind !== buffer.type) {
      flush();
      buffer = { type: kind, lines: [line] };
    } else {
      buffer.lines.push(line);
    }
  }
  flush();
  return blocks;
}

export default function ForumPostBody({ body }) {
  if (!body) return null;

  // YouTube embeds — one per unique ID, appended after the body.
  const ytIds = [];
  const seen = new Set();
  for (const m of body.matchAll(URL_RE)) {
    const id = extractYouTubeId(m[0]);
    if (id && !seen.has(id)) {
      seen.add(id);
      ytIds.push(id);
    }
  }

  const blocks = parseBlocks(body);

  return (
    <>
      {blocks.map((b, i) => {
        const text = b.lines.join("\n");
        if (b.type === "quote") {
          return (
            <blockquote
              key={`b-${i}`}
              className="my-2 whitespace-pre-wrap break-words border-l-2 border-[#6CABDD] bg-[#6CABDD]/5 px-3 py-1.5 text-[13px] italic text-[var(--brand-body)]"
              data-testid="forum-quote-block"
            >
              {linkify(text, `b${i}-`)}
            </blockquote>
          );
        }
        return (
          <p key={`b-${i}`} className="whitespace-pre-wrap break-words text-sm">
            {linkify(text, `b${i}-`)}
          </p>
        );
      })}
      {ytIds.length > 0 ? (
        <div className="mt-3 space-y-3" data-testid="forum-post-youtube">
          {ytIds.map((id) => (
            <div
              key={id}
              className="relative w-full overflow-hidden rounded-md bg-black"
              style={{ paddingBottom: "56.25%" /* 16:9 */ }}
            >
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${id}`}
                title="YouTube video"
                className="absolute inset-0 h-full w-full"
                loading="lazy"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}
